/**
 * Step 2: Discount Configuration
 * 
 * Configure discount type, level, and value
 * - Discount Level: Item or Order
 * - Discount Type: Percentage, Fixed Amount, Buy X Get Y (disabled)
 * - Dynamic fields based on selected discount type
 * - Live preview of promotion
 */

import React, { useMemo, useRef } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import { PromoFormData, PromoValidationErrors, BenefitType, BenefitLevel, PromoDomain } from '../../../../../api/promos/types';
import { useCatalogueConfig } from '../../../../../contexts/CatalogueConfigContext';
import '../../../Campaigns/CreateCampaign/components/StepperButtons.css';
import '../../../Campaigns/CreateCampaign/components/StepperFormStyles.css';
import './DiscountTypeButtons.css';

interface StepDiscountConfigProps {
  formData: PromoFormData;
  validationErrors: PromoValidationErrors;
  onChange: (updates: Partial<PromoFormData>) => void;
  onNext: () => void;
  onPrevious: () => void;
}

// Discount type enum for UI (not the same as BenefitType)
enum DiscountTypeUI {
  PERCENTAGE = 'PERCENTAGE',
  FIXED_AMOUNT = 'FIXED_AMOUNT',
  BUY_X_GET_Y = 'BUY_X_GET_Y',
}

const StepDiscountConfig: React.FC<StepDiscountConfigProps> = ({
  formData,
  validationErrors,
  onChange,
  onNext,
  onPrevious,
}) => {
  // Fetch catalogue config for categories
  const { config, loading: configLoading } = useCatalogueConfig();

  const isOrderOnlyDomain = formData.domain === PromoDomain.ACADEMY || formData.domain === PromoDomain.BOOKING;

  // Force discount level to ORDER for Academy and Booking domains
  React.useEffect(() => {
    if (isOrderOnlyDomain && formData.benefit.level !== BenefitLevel.ORDER) {
      onChange({
        benefit: {
          ...formData.benefit,
          level: BenefitLevel.ORDER,
          applicableCatalogueCategory: undefined,
        },
      });
    }
  }, [isOrderOnlyDomain]);

  // Refs to track input values and prevent browser autocomplete changes
  const percentageValueRef = useRef<string>(String(formData.benefit.value || ''));
  const maxDiscountRef = useRef<string>(String(formData.benefit.maxDiscountAmount || ''));
  const fixedAmountRef = useRef<string>(String(formData.benefit.value || ''));
  
  // Track which fields are currently focused to prevent mouse handlers from interfering
  const percentageFocusedRef = useRef<boolean>(false);
  const maxDiscountFocusedRef = useRef<boolean>(false);
  const fixedAmountFocusedRef = useRef<boolean>(false);

  // Update refs when formData changes
  React.useEffect(() => {
    percentageValueRef.current = String(formData.benefit.value || '');
    maxDiscountRef.current = String(formData.benefit.maxDiscountAmount || '');
    fixedAmountRef.current = String(formData.benefit.value || '');
  }, [formData.benefit.value, formData.benefit.maxDiscountAmount]);
  // Map BenefitType to UI DiscountType
  const getCurrentDiscountType = (): DiscountTypeUI => {
    if (formData.benefit.type === BenefitType.PERCENTAGE_DISCOUNT) {
      return DiscountTypeUI.PERCENTAGE;
    } else if (formData.benefit.type === BenefitType.FIXED_DISCOUNT || formData.benefit.type === BenefitType.FLAT_DISCOUNT) {
      return DiscountTypeUI.FIXED_AMOUNT;
    }
    return DiscountTypeUI.PERCENTAGE; // Default
  };

  const currentDiscountType = getCurrentDiscountType();

  // Handle discount type change
  const handleDiscountTypeChange = (type: DiscountTypeUI) => {
    if (type === DiscountTypeUI.BUY_X_GET_Y) {
      return;
    }

    const updates: Partial<PromoFormData> = {
      benefit: {
        ...formData.benefit,
        type: type === DiscountTypeUI.PERCENTAGE 
          ? BenefitType.PERCENTAGE_DISCOUNT 
          : BenefitType.FIXED_DISCOUNT,
        value: type === DiscountTypeUI.PERCENTAGE ? 0 : formData.benefit.value || 0,
        maxDiscountAmount: type === DiscountTypeUI.PERCENTAGE ? formData.benefit.maxDiscountAmount || 0 : undefined,
      }
    };
    onChange(updates);
  };

  // Handle benefit field changes
  const handleBenefitChange = (field: keyof typeof formData.benefit, value: any) => {
    onChange({
      benefit: {
        ...formData.benefit,
        [field]: value,
      }
    });
  };

  // Prepare category options from config
  const categoryOptions = useMemo(() => {
    if (!config) return [];
    return [
      ...config.categories.apparels.map(cat => ({ value: cat.value, label: cat.label })),
      ...config.categories.accessories.map(cat => ({ value: cat.value, label: cat.label }))
    ];
  }, [config]);

  // Handle discount level change
  const handleLevelChange = (level: BenefitLevel) => {
    onChange({
      benefit: {
        ...formData.benefit,
        level: level,
        // Clear category when switching from ITEM to ORDER
        applicableCatalogueCategory: level === BenefitLevel.ITEM 
          ? formData.benefit.applicableCatalogueCategory 
          : undefined,
      }
    });
  };

  // Order value options for Buy X Get Y
  const orderValueOptions = [
    { value: 'ORDER_VALUE', label: 'Order Value (₹)' },
    { value: 'SESSIONS', label: 'Sessions' },
    { value: 'ITEMS', label: 'Items' },
    { value: 'BOOKINGS', label: 'Bookings' },
    { value: 'SLOTS', label: 'Slots' },
    { value: 'DAYS', label: 'Days' },
    { value: 'WEEKS', label: 'Weeks' },
    { value: 'MONTHS', label: 'Months' },
  ];

  // React-select styles (matching filter dropdowns)
  const getSelectStyles = () => ({
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    control: (base: any) => ({
      ...base,
      height: '38px',
      minHeight: '38px',
      border: '1px solid #e3e6f0',
      borderRadius: '6px',
      fontSize: '14px',
      boxShadow: 'none',
      width: '100%',
      borderColor: '#e3e6f0',
      '&:hover': {
        borderColor: '#e3e6f0'
      }
    }),
    valueContainer: (base: any) => ({
      ...base,
      height: '36px',
      padding: '0 8px',
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      height: '36px',
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      padding: '0 8px',
    }),
    singleValue: (base: any) => ({
      ...base,
      color: '#212529',
      fontSize: '14px',
    }),
    placeholder: (base: any) => ({
      ...base,
      color: '#6c757d',
      fontSize: '14px'
    }),
  });

  // Generate preview text
  const getPreviewText = (): string => {
    if (currentDiscountType === DiscountTypeUI.PERCENTAGE) {
      const percentage = formData.benefit.value || 0;
      const maxCap = formData.benefit.maxDiscountAmount || 0;
      if (maxCap > 0) {
        return `Get ${percentage}% OFF (Max ₹${maxCap})`;
      }
      return `Get ${percentage}% OFF`;
    } else if (currentDiscountType === DiscountTypeUI.FIXED_AMOUNT) {
      const amount = formData.benefit.value || 0;
      return `Get Flat ₹${amount} OFF`;
    } else if (currentDiscountType === DiscountTypeUI.BUY_X_GET_Y) {
      // Buy X Get Y preview (when implemented)
      return 'Buy X Get Y';
    }
    return '';
  };

  // Validation: Check if required fields are filled
  const isNextDisabled = useMemo(() => {
    if (!formData.benefit.type) return true;
    if (!formData.listingTitle?.trim()) return true;
    if (!formData.appliedTitle?.trim()) return true;

    if (currentDiscountType === DiscountTypeUI.PERCENTAGE) {
      return !formData.benefit.value || formData.benefit.value <= 0;
    } else if (currentDiscountType === DiscountTypeUI.FIXED_AMOUNT) {
      return !formData.benefit.value || formData.benefit.value <= 0;
    }
    
    return false;
  }, [formData.benefit, formData.listingTitle, formData.appliedTitle, currentDiscountType]);

  return (
    <form autoComplete="off" noValidate>
      {/* Step Header */}
      <div className="mb-4">
        <h5 className="mb-2">Discount Configuration</h5>
        <p className="text-muted mb-0">Define how the discount will be calculated</p>
      </div>

      {/* Form Fields */}
      <Row>
        {/* Discount Level */}
        <Col lg={12} className="mb-4">
          <Form.Group className="stepper-form-group">
            <Form.Label className="stepper-form-label">Discount Level</Form.Label>
            <div className="d-flex gap-4 mt-2">
              <Form.Check
                type="radio"
                id="level-item"
                label="Item"
                checked={formData.benefit.level === BenefitLevel.ITEM}
                onChange={() => handleLevelChange(BenefitLevel.ITEM)}
                className="stepper-radio"
                disabled={isOrderOnlyDomain}
              />
              <Form.Check
                type="radio"
                id="level-order"
                label="Order"
                checked={formData.benefit.level === BenefitLevel.ORDER}
                onChange={() => handleLevelChange(BenefitLevel.ORDER)}
                className="stepper-radio"
                disabled={isOrderOnlyDomain}
              />
            </div>
            {isOrderOnlyDomain && (
              <small className="text-muted mt-1 d-block">
                Discount level is fixed to <strong>Order</strong> for {formData.domain === PromoDomain.ACADEMY ? 'Academy' : 'Booking'} domain.
              </small>
            )}
          </Form.Group>
        </Col>

        {/* Discount Type */}
        <Col lg={12} className="mb-4">
          <Form.Group className="stepper-form-group">
            <Form.Label className="stepper-form-label">Discount Type</Form.Label>
            <div className="discount-type-buttons-container">
              <button
                type="button"
                className={`discount-type-button ${
                  currentDiscountType === DiscountTypeUI.PERCENTAGE ? 'selected' : ''
                }`}
                onClick={() => handleDiscountTypeChange(DiscountTypeUI.PERCENTAGE)}
              >
                Percentage
              </button>
              <button
                type="button"
                className={`discount-type-button ${
                  currentDiscountType === DiscountTypeUI.FIXED_AMOUNT ? 'selected' : ''
                }`}
                onClick={() => handleDiscountTypeChange(DiscountTypeUI.FIXED_AMOUNT)}
              >
                Fixed Amount
              </button>
              <button
                type="button"
                className="discount-type-button"
                disabled
                title="Coming soon"
              >
                Buy X Get Y
              </button>
            </div>
          </Form.Group>
        </Col>

        {/* Listing Title and Applied Title */}
        <Col lg={6} className="mb-3">
          <Form.Group className="stepper-form-group">
            <Form.Label className="stepper-form-label">
              Listing Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              className="stepper-form-control"
              placeholder="e.g., Flat 20% OFF on Apparel"
              value={formData.listingTitle || ''}
              onChange={(e) => onChange({ listingTitle: e.target.value })}
              isInvalid={!!validationErrors.listingTitle}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
            <Form.Control.Feedback type="invalid" className="stepper-invalid-feedback">
              {validationErrors.listingTitle}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col lg={6} className="mb-3">
          <Form.Group className="stepper-form-group">
            <Form.Label className="stepper-form-label">
              Applied Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              className="stepper-form-control"
              placeholder="e.g., 20% OFF Applied"
              value={formData.appliedTitle || ''}
              onChange={(e) => onChange({ appliedTitle: e.target.value })}
              isInvalid={!!validationErrors.appliedTitle}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
            <Form.Control.Feedback type="invalid" className="stepper-invalid-feedback">
              {validationErrors.appliedTitle}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        {/* Percentage Discount Fields */}
        {currentDiscountType === DiscountTypeUI.PERCENTAGE && (
          <>
            <Col lg={6} className="mb-3">
              <Form.Group className="stepper-form-group">
                <Form.Label className="stepper-form-label">
                  Percentage Value (%) <span className="text-danger">*</span>
                </Form.Label>
                <div className="input-group">
                  <Form.Control
                    type="number"
                    className={`stepper-form-control ${validationErrors.benefit?.value ? 'is-invalid' : ''}`}
                    placeholder="0"
                    value={formData.benefit.value || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      percentageValueRef.current = val;
                      handleBenefitChange('value', val ? parseFloat(val) : 0);
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      percentageValueRef.current = val;
                      if (val !== String(formData.benefit.value || '')) {
                        handleBenefitChange('value', val ? parseFloat(val) : 0);
                      }
                    }}
                    onWheel={(e) => {
                      // Prevent scroll from changing number input values
                      e.currentTarget.blur();
                    }}
                    min="0"
                    max="100"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    inputMode="numeric"
                    data-lpignore="true"
                    data-form-type="other"
                  />
                  <span className="input-group-text">%</span>
                </div>
                <Form.Control.Feedback type="invalid" className="stepper-invalid-feedback">
                  {validationErrors.benefit?.value}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6} className="mb-3">
              <Form.Group className="stepper-form-group">
                <Form.Label className="stepper-form-label">
                  Max Discount Cap (₹) <span className="text-muted">(Optional)</span>
                </Form.Label>
                <div className="input-group">
                  <span className="input-group-text">₹</span>
                  <Form.Control
                    type="number"
                    className="stepper-form-control"
                    placeholder="0"
                    value={formData.benefit.maxDiscountAmount || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      maxDiscountRef.current = val;
                      handleBenefitChange('maxDiscountAmount', val ? parseFloat(val) : undefined);
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      maxDiscountRef.current = val;
                      if (val !== String(formData.benefit.maxDiscountAmount || '')) {
                        handleBenefitChange('maxDiscountAmount', val ? parseFloat(val) : undefined);
                      }
                    }}
                    onWheel={(e) => {
                      // Prevent scroll from changing number input values
                      e.currentTarget.blur();
                    }}
                    min="0"
                    isInvalid={!!validationErrors.benefit?.maxDiscountAmount}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    inputMode="numeric"
                    data-lpignore="true"
                    data-form-type="other"
                  />
                </div>
                <Form.Control.Feedback type="invalid" className="stepper-invalid-feedback">
                  {validationErrors.benefit?.maxDiscountAmount}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            {/* Category field - only shown when discount level is ITEM */}
            {formData.benefit?.level === BenefitLevel.ITEM && (
              <Col lg={6} className="mb-3">
                <Form.Group className="stepper-form-group">
                  <Form.Label className="stepper-form-label">
                    Category <span className="text-muted">(Optional)</span>
                  </Form.Label>
                  <Select
                    options={categoryOptions}
                    value={categoryOptions.find(opt => opt.value === formData.benefit?.applicableCatalogueCategory) || null}
                    onChange={(selected) => {
                      handleBenefitChange('applicableCatalogueCategory', selected?.value || undefined);
                    }}
                    isClearable
                    isSearchable
                    placeholder="Select category"
                    isLoading={configLoading}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '38px',
                        borderColor: validationErrors.benefit?.applicableCatalogueCategory ? '#dc3545' : '#ced4da',
                        '&:hover': {
                          borderColor: validationErrors.benefit?.applicableCatalogueCategory ? '#dc3545' : '#ced4da',
                        },
                      }),
                    }}
                  />
                  {validationErrors.benefit?.applicableCatalogueCategory && (
                    <div className="stepper-invalid-feedback d-block">
                      {validationErrors.benefit.applicableCatalogueCategory}
                    </div>
                  )}
                </Form.Group>
              </Col>
            )}
          </>
        )}

        {/* Fixed Amount Discount Fields */}
        {currentDiscountType === DiscountTypeUI.FIXED_AMOUNT && (
          <>
            <Col lg={6} className="mb-3">
              <Form.Group className="stepper-form-group">
                <Form.Label className="stepper-form-label">
                  Fixed Amount (₹) <span className="text-danger">*</span>
                </Form.Label>
                <div className="input-group">
                  <span className="input-group-text">₹</span>
                  <Form.Control
                    type="number"
                    className={`stepper-form-control ${validationErrors.benefit?.value ? 'is-invalid' : ''}`}
                    placeholder="0"
                    value={formData.benefit.value || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      fixedAmountRef.current = val;
                      handleBenefitChange('value', val ? parseFloat(val) : 0);
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      fixedAmountRef.current = val;
                      if (val !== String(formData.benefit.value || '')) {
                        handleBenefitChange('value', val ? parseFloat(val) : 0);
                      }
                    }}
                    onWheel={(e) => {
                      // Prevent scroll from changing number input values
                      e.currentTarget.blur();
                    }}
                    min="0"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    inputMode="numeric"
                    data-lpignore="true"
                    data-form-type="other"
                  />
                </div>
                <Form.Control.Feedback type="invalid" className="stepper-invalid-feedback">
                  {validationErrors.benefit?.value}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            {/* Category field - only shown when discount level is ITEM */}
            {formData.benefit?.level === BenefitLevel.ITEM && (
              <Col lg={6} className="mb-3">
                <Form.Group className="stepper-form-group">
                  <Form.Label className="stepper-form-label">
                    Category <span className="text-muted">(Optional)</span>
                  </Form.Label>
                  <Select
                    options={categoryOptions}
                    value={categoryOptions.find(opt => opt.value === formData.benefit?.applicableCatalogueCategory) || null}
                    onChange={(selected) => {
                      handleBenefitChange('applicableCatalogueCategory', selected?.value || undefined);
                    }}
                    isClearable
                    isSearchable
                    placeholder="Select category"
                    isLoading={configLoading}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '38px',
                        borderColor: validationErrors.benefit?.applicableCatalogueCategory ? '#dc3545' : '#ced4da',
                        '&:hover': {
                          borderColor: validationErrors.benefit?.applicableCatalogueCategory ? '#dc3545' : '#ced4da',
                        },
                      }),
                    }}
                  />
                  {validationErrors.benefit?.applicableCatalogueCategory && (
                    <div className="stepper-invalid-feedback d-block">
                      {validationErrors.benefit.applicableCatalogueCategory}
                    </div>
                  )}
                </Form.Group>
              </Col>
            )}
          </>
        )}

        {/* Buy X Get Y Fields (Disabled for now) */}
        {currentDiscountType === DiscountTypeUI.BUY_X_GET_Y && (
          <>
            <Col lg={12} className="mb-3">
              <Form.Label className="stepper-form-label">Customer Buys</Form.Label>
              <Row>
                <Col lg={6}>
                  <Form.Control
                    type="number"
                    className="stepper-form-control"
                    placeholder="0"
                    disabled
                  />
                </Col>
                <Col lg={6}>
                  <Select
                    styles={getSelectStyles()}
                    options={orderValueOptions}
                    placeholder="Order Value (₹)"
                    menuPortalTarget={document.body}
                    isDisabled
                  />
                </Col>
              </Row>
            </Col>
            <Col lg={12} className="mb-3">
              <Form.Label className="stepper-form-label">Customer Gets</Form.Label>
              <Row>
                <Col lg={4}>
                  <Form.Control
                    type="number"
                    className="stepper-form-control"
                    placeholder="0"
                    disabled
                  />
                </Col>
                <Col lg={4}>
                  <Select
                    styles={getSelectStyles()}
                    options={orderValueOptions}
                    placeholder="Item"
                    menuPortalTarget={document.body}
                    isDisabled
                  />
                </Col>
                <Col lg={4}>
                  <Select
                    styles={getSelectStyles()}
                    options={[{ value: 'FREE', label: 'Free' }]}
                    placeholder="Free"
                    menuPortalTarget={document.body}
                    isDisabled
                  />
                </Col>
              </Row>
            </Col>
          </>
        )}

        {/* Promotion Preview */}
        <Col lg={12} className="mb-4">
          <div className="discount-preview-box">
            <div className="discount-preview-label">
              This is how customers will see this promotion
            </div>
            {currentDiscountType === DiscountTypeUI.FIXED_AMOUNT ? (
              <span className="discount-preview-badge">
                {getPreviewText()}
              </span>
            ) : (
              <div className="discount-preview-text">
                {getPreviewText() || 'Enter discount details to see preview'}
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Navigation Buttons */}
      <div className="d-flex justify-content-between mt-4">
        <button className="stepper-back-button" onClick={onPrevious}>
          <i className="ph-arrow-left"></i>
          Back
        </button>
        <button 
          className="stepper-next-button" 
          onClick={onNext}
          disabled={isNextDisabled}
        >
          Next
          <i className="ph-arrow-right"></i>
        </button>
      </div>
    </form>
  );
};

export default StepDiscountConfig;
